import type { HandoverOut } from "@/api";
import { Figures } from "@/components/Figures";
import { activityTime, formatDay } from "@/lib/format";

import styles from "./Handover.module.css";

interface HandoverBoxProps {
  handover: HandoverOut | null;
  /**
   * The last acceptance of this Vorgang — who handed it over, and when (UX
   * gate round 2, #3). Read off the record's timeline by the caller; `null`
   * for a Vorgang never taken over.
   */
  taken?: { from: string; at: string } | null;
}

/**
 * The fact sheet's „Übergabe" box (design §8.2/§8.3): the one open request
 * with its recipient, since when, until when and its message, and where the
 * Vorgang came from. The head says the same in one line; this is the detail.
 *
 * Nothing where there is nothing to say (UX-Gutachten 2, B-8): „Keine
 * offene Anfrage." took a section of the sheet to say nothing, and under a
 * banner the sheet ran out of room for what it holds.
 *
 * Only the state (UX gate round 3, #8): „Anfrage zurückziehen" stood here
 * and in the head's waiting line — two equal buttons read as two requests.
 * The way back is the waiting line's alone, where the eye is and where it
 * stays when the sheet is folded on a phone.
 */
export function HandoverBox({ handover, taken = null }: HandoverBoxProps) {
  if (handover === null && taken === null) return null;
  return (
    <section className={styles.box} aria-labelledby="handover-heading">
      <h2 id="handover-heading" className="label">
        Übergabe
      </h2>
      {/* Where the Vorgang came from stays in view after the acceptance — the
          open request's box, which said so, is empty by then. */}
      {taken !== null ? (
        <p className={styles.line}>
          Übernommen von <b>{taken.from}</b> am{"\u00a0"}
          <span className="tabular">{formatDay(taken.at)}</span>
        </p>
      ) : null}
      {handover !== null ? (
        <div className={styles.pending}>
          <p className={styles.line}>
            <b>Angefragt an {handover.toDisplayName}</b>
          </p>
          {/* Times and dates in the figure face, as on the start page's card (UX gate round 1, #13). */}
          <p className={styles.sub}>
            <Figures text={`seit ${activityTime(handover.requestedAt)}`} />
          </p>
          <p className={styles.sub}>
            <Figures text={`verfällt am ${formatDay(handover.expiresAt)}`} />
          </p>
          <blockquote className={styles.quote}>{handover.message}</blockquote>
        </div>
      ) : null}
    </section>
  );
}
